import * as React from "react";
import { Select, IOption } from "../../atoms/forms/Select";
import { IApplicationState } from "../../../store";
import * as Languages from "../../../store/languages";
import { FetchState } from "../../../entities/FetchState";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { IData as LanguageData } from '../../../store/languages/languagesList';
import { IStoreSelectControls } from "./ICommonInputsControls";

// Language interface from the store
type Language = LanguageData;


interface ILanguage extends IOption {
    language: Language;
}

const mapLanguageToValue = (language: Language): ILanguage => {
    return {
        value: language.id,
        label: language.description,
        language: language
    }
}

// Props interface
interface IProps {
    selectedLanguageId?: string;
}

// Actual Component
export type AllProps = IProps & IStoreSelectControls<Language>;
export const LanguageSelect = (props: AllProps) => {
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tCommon } = useTranslation('common');
    
    // Get Redux state and dispatch
    const languagesState = useSelector((state: IApplicationState) => state.languages.languagesList);
    const dispatch = useDispatch();

    // Internal state for selected language - initialize with preselected language if available
    const [selectedLanguage, setSelectedLanguage] = React.useState<ILanguage | null>(() => {
        // Try to initialize with preselected language if data is already available
        if (languagesState.LanguagesList.data && props.selectedLanguageId) {
            const language = languagesState.LanguagesList.data.find(l => l.id === props.selectedLanguageId);
            if (language) {
                return mapLanguageToValue(language);
            }
        }
        // If using value from common interface
        else if (props.value) {
            return mapLanguageToValue(props.value);
        }
        return null;
    });

    // Fetch languages on initial render if not loaded or if the data array is empty
    React.useEffect(() => {
        const shouldFetch = 
            languagesState.LanguagesList.state === FetchState.NotFetched || 
            (languagesState.LanguagesList.state === FetchState.Success && 
             (!languagesState.LanguagesList.data || languagesState.LanguagesList.data.length === 0));
             
        if (shouldFetch) {
            dispatch(Languages.ActionCreators.languagesList.FetchData());
        }
    }, [dispatch, languagesState.LanguagesList.state, languagesState.LanguagesList.data]);

    // Update selected language when data is loaded or selectedLanguageId prop changes
    React.useEffect(() => {
        if (languagesState.LanguagesList.data) {
            if (props.selectedLanguageId) {
                const language = languagesState.LanguagesList.data.find(l => l.id === props.selectedLanguageId);
                if (language) {
                    const mappedLanguage = mapLanguageToValue(language);
                    setSelectedLanguage(mappedLanguage);
                } else {
                    setSelectedLanguage(null);
                }
            } else if (props.value) {
                // Handle value from common interface
                setSelectedLanguage(mapLanguageToValue(props.value));
            } else {
                setSelectedLanguage(null);
            }
        }
    }, [languagesState.LanguagesList.data, props.selectedLanguageId, props.value, languagesState.LanguagesList.state]);

    // Map languages to select options
    const languages = React.useMemo(() => {
        return (languagesState.LanguagesList.data || [])
            .map(mapLanguageToValue)
            .sort((a, b) => {
                if (a.label && b.label) {
                    return a.label.localeCompare(b.label);
                }
                // Fallback: compare by id if label is undefined
                return String(a.value).localeCompare(String(b.value));
            });
    }, [languagesState.LanguagesList.data, languagesState.LanguagesList.state]);

    // Force component re-render when languages list changes state from loading to success
    const [forceUpdateKey, setForceUpdateKey] = React.useState(0);
    React.useEffect(() => {
        if (languagesState.LanguagesList.state === FetchState.Success && languagesState.LanguagesList.data && languagesState.LanguagesList.data.length > 0) {
            setForceUpdateKey(prev => prev + 1);
        }
    }, [languagesState.LanguagesList.state, languagesState.LanguagesList.data]);

    const updateLanguage = (language: ILanguage | null) => {
        setSelectedLanguage(language);
        
        // Call onChange callback from common interface
        if (props.onChange) {
            props.onChange(language?.language || null, !props.required || !!language);
        }
    }

    const isDisabled = props.disabled || languagesState.LanguagesList.state !== FetchState.Success;
    const isLoading = languagesState.LanguagesList.state === FetchState.Busy || languagesState.LanguagesList.state === FetchState.NotFetched || props.loading;
    const error = props.error || (languagesState.LanguagesList.state === FetchState.Error ? [true, tCommon('errorLoadingData')] : undefined);

    return (<Select 
        key={forceUpdateKey}
        label={(props.label || tDatatypes('languageLabel') || "Language") + (languagesState.LanguagesList.state !== FetchState.Success ? tCommon('loading') : "")}
        placeholder={props.placeholder || (tDatatypes('languagePlaceholder') || "Select a language...")}
        options={languages}
        value={selectedLanguage}
        disabled={isDisabled}
        onChange={updateLanguage}
        loading={isLoading}
        error={error}
        required={props.required}
        prevalidate={props.prevalidate}
        icon={props.icon}
        isUnknownValuesEnabled={props.isUnknownValuesEnabled}
    />)
}

